import React, { useState } from 'react';
import { Input, makeStyles, Modal } from '@material-ui/core';
import useStyles from '../KeyTypes/CommonStyle';
import { ZuiIconEditEditEdit } from '@zeiss/zui-react';
import { getSubscription, getESBApiBaseUrl } from 'Utils/getBaseUrl';
import { useSelector } from 'react-redux';

const useMakeStyles = makeStyles((theme) => ({
    modal: {
        '&:focus': {
            outline: 'none',
        },
        '&:active': {
            outline: 'none',
        },
        background: '#1a23293b !important',
        zIndex: '30000 !important',
    },
    modalPaper: {
        position: 'absolute',
        [theme.breakpoints.up('xs')]: {
            height: 239,
            width: 294,
        },
        [theme.breakpoints.up('md')]: {
            width: '90%',
            height: '80%',
        },
        backgroundColor: theme.palette.background.paper,
        borderTop: '5px solid #0050F2',
        boxShadow: theme.shadows[5],
        border: 'none',
        padding: '0px',
        textAlign: 'center',
        borderRadius: '5px',
        outline: 'none',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'column',
    },
    modalHeader: {
        textAlign: 'center',
        border: '0px',
        width: '100%',
        borderBottom: '1px solid #DDE3ED',
        padding: '15px 0',
        [theme.breakpoints.up('xs')]: {
            // marginTop: '15px',
            marginBottom: '15px',
        },
        [theme.breakpoints.up('md')]: {
            // marginTop: '20px',
            marginBottom: '20px',
        },
    },
    modalBody: {
        position: 'relative',
        padding: '0px 25px',
        textAlign: 'center',
        width: '75%',
        // height: 'calc(100% - 155px)',
    },
    showErrorMessage: {
        width: '100%',
        padding: '5px 0px',
        font: 'var(--zui-typography-body)',
        fontSize: '14px',
        lineHeight: '20px',
        textAlign: 'center',
        color: '#e62e2eff',
    },
    messagebody: {
        width: 'inherit',
        position: 'relative',
        display: 'block',
        [theme.breakpoints.up('xs')]: {
            margin: '15px 0px',
        },
        [theme.breakpoints.up('md')]: {
            margin: '30px 0px',
        },
    },
    modalFooter: {
        marginTop: 'auto',
        borderTop: '1px solid #DDE3ED',
        alignSelf: 'center',
        display: 'flex',
        width: '100%',
        textAlign: 'center',
    },
    keyFormContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2rem',
    },
    keyFormChildren: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        width: '100%',
        flexDirection: 'column',
    },
    keyFormChildrenAutocomplete: {
        display: 'block',
        width: '100%',
    },
}));

function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
}

export default function EditMachineNameModal({
    openState,
    clickClose,
    machine,
    onEditMachineName,
}) {
    const classes = useMakeStyles();
    const commonClasses = useStyles();
    const [modalStyle] = useState(getModalStyle);
    const [newMachineName, setNewMachineName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationError, setValidationError] = useState('');

    const esbApiUrl = getESBApiBaseUrl();
    const token = useSelector((state) => state.userAccount.tokenId);

    const handleChangeInput = (e) => {
        const value = e.target.value;
        setNewMachineName(value);

        // Regex for validation: Allows letters, numbers, space, dot, underscore and hyphen.
        const validationRegex = /^[a-zA-Z0-9\s._-]*$/;

        if (!validationRegex.test(value)) {
            setValidationError(
                'Invalid characters. Only letters, numbers, spaces, dots, underscores and hyphens are allowed.'
            );
        } else if (value.length > 64) {
            setValidationError('Input is too long. Maximum 64 characters allowed.');
        } else {
            setValidationError(''); // Fehler löschen, wenn die Eingabe gültig ist
        }
    };

    const handleSendData = async (value) => {
        setLoading(true); // Set loading to true

        try {
            if (!esbApiUrl) {
                throw new Error(
                    'API base URL is not configured. Check getESBApiBaseUrl().'
                );
            }
            const esbSubscriptionKey = getSubscription();

            const finalUrl = `${esbApiUrl}/UpdateDeviceName`;
            const payload = {
                deviceId: machine?.id,
                deviceNewName: newMachineName,
            };

            const response = await fetch(finalUrl, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'EsbApi-Subscription-Key': esbSubscriptionKey,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                // Versuche, eine detailliertere Fehlermeldung vom Server zu erhalten
                let errorMsg = `Request failed with status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.message + ' - ' + errorMsg;
                } catch (e) {
                    // Ignorieren, wenn der Body kein JSON ist
                }
                throw new Error(errorMsg);
            } else {
                setNewMachineName('');
                if (onEditMachineName) onEditMachineName();
                handleClose();
            }

            // Close the modal after sending data
        } catch (error) {
            // console.error('Error editing machine:', error);
            // setError('Failed to edit machine. Please try again later.' + error);
            console.error('Error editing machine:', error);
            setError(error.message || 'Failed to edit machine. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setNewMachineName('');
        setError('');
        setLoading(false);
        clickClose();
    };

    return (
        <>
            <Modal
                disableAutoFocus={true}
                className={classes.modal}
                open={openState}
                onClose={() => {
                    clickClose();
                }}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
            >
                <div style={modalStyle} className={classes.modalPaper}>
                    <div
                        className={classes.modalHeader}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <ZuiIconEditEditEdit size="xl"></ZuiIconEditEditEdit>

                        <p>
                            Edit Machine with icCode: <b>{machine?.icCode}</b>
                        </p>
                    </div>
                    <div className={classes.modalBody}>
                        <form action="" type="submit" className={classes.keyFormContainer}>
                            <div className={classes.keyFormChildrenAutocomplete}>
                                <Input
                                    type="text"
                                    name="name"
                                    placeholder="Name"
                                    value={newMachineName}
                                    onChange={handleChangeInput}
                                    required
                                    style={{ width: '50%' }}
                                />
                                {validationError && (
                                    <p className={classes.showErrorMessage}>{validationError}</p>
                                )}
                            </div>
                        </form>
                        <div>
                            <p className={classes.showErrorMessage}>{error}</p>
                        </div>
                    </div>
                    <div className={`${classes.modalFooter}`}>
                        <button
                            type="button"
                            className="btn-modal modalbtn"
                            onClick={handleClose}
                        >
                            {'Cancel'}
                        </button>
                        <button
                            disabled={newMachineName === '' || loading || validationError}
                            type="button"
                            className={`${commonClasses.btnSubmit} ${(!newMachineName || loading || validationError) &&
                                commonClasses.btnDisabled
                                } btn-modal-submit`}
                            onClick={handleSendData}
                        >
                            {loading ? 'Editing...' : 'Edit'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
